import React, { useEffect, useState } from "react";

// ─── University semester dates — MARCH 2027 (2027-1) ──────────────────────
// ⚠️ ESTIMATED, NOT OFFICIAL. As of July 2026, no Lima university has
// published its 2027-1 academic calendar yet — only 2026 dates exist.
// These are placeholders based on each school's typical March start
// pattern from 2025/2026 calendars. Swap in real dates the moment each
// school publishes them (usually announced Oct–Dec the prior year).
const UNIVERSITIES = [
  { name: "PUCP",   date: new Date("2027-03-22"), confirmed: false },
  { name: "UPC",    date: new Date("2027-03-16"), confirmed: false },
  { name: "U.Lima", date: new Date("2027-03-16"), confirmed: false },
  { name: "UNMSM",  date: new Date("2027-03-23"), confirmed: false },
];

// ─── Phase-based messaging ──────────────────────────────────────────────────
// Tone shifts as the semester approaches. Scarcity language only kicks in
// once it's actually credible (inside ~8 weeks).
type Phase = "early" | "mid" | "urgent";

const MESSAGES: Record<Phase, string[]> = {
  early: [
    "Best rooms for March 2027 go first",
    "March 2027 listings are live — browse now",
    "Get ahead of the March 2027 rush",
  ],
  mid: [
    "Popular buildings are booking up for March",
    "Best rooms for March 2027 are going first",
    "Reserve now to guarantee your top choice",
  ],
  urgent: [
    "Secure your room before it's too late",
    "Most students book 8 weeks before semester",
    "Properties filling up fast for March",
  ],
};

const getPhase = (weeksOut: number): Phase => {
  if (weeksOut <= 8) return "urgent";
  if (weeksOut <= 20) return "mid";
  return "early";
};

// ─── Featured listings ──────────────────────────────────────────────────────
// Swap these hrefs for the real listing URLs on your platform.
const FEATURED_LISTINGS = [
  { name: "Casa Madrid", href: "/listing-stay-detail/casa-madrid-18-bedroom-student-house-miraflores/" },
  { name: "Casa Pisco",  href: "/listing-stay-detail/casa-pisco-in-miraflores-5-bedroom-student-house/" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const getDaysRemaining = (targetDate: Date): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = targetDate.getTime() - today.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Under 8 weeks: show days ("54d"). Otherwise show weeks ("18w") — reads
// clearer than a big three-digit day count this far out.
const formatCountdown = (days: number): string => {
  if (days <= 56) return `${days}d`;
  return `${Math.ceil(days / 7)}w`;
};

// ─── Component ──────────────────────────────────────────────────────────────
const SemesterCountdownBarMarch2027: React.FC = () => {
  const [days, setDays] = useState<number[]>(
    UNIVERSITIES.map((u) => getDaysRemaining(u.date))
  );
  const [messageIndex, setMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  // Earliest upcoming date drives the overall phase/tone of the banner
  const minDays = Math.min(...days);
  const phase = getPhase(minDays / 7);
  const messages = MESSAGES[phase];

  // Recalculate at midnight so the countdown stays accurate
  useEffect(() => {
    const update = () =>
      setDays(UNIVERSITIES.map((u) => getDaysRemaining(u.date)));

    update();

    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - now.getTime();

    const timeout = setTimeout(() => {
      update();
      const interval = setInterval(update, 24 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  }, []);

  // Rotate messages every 4s; reset index if phase (and thus message pool) changes
  useEffect(() => {
    setMessageIndex(0);
  }, [phase]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
        setFade(true);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [messages.length]);

  if (days.every((d) => d === 0)) return null;

  return (
    <div className="
      w-full py-3 px-4
      bg-gradient-to-r from-orange-50 via-rose-50 to-amber-50
      border-b border-orange-200/60
      dark:bg-gradient-to-r dark:from-[#1a1a2e] dark:via-[#16213e] dark:to-[#0f3460] dark:border-transparent
    ">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-3 sm:gap-0 sm:justify-between">

        {/* ── Left: icon + rotating, phase-aware message ── */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-base flex-shrink-0">🎓</span>
          <span
            className="font-semibold text-sm text-gray-800 dark:text-white whitespace-nowrap transition-opacity duration-300"
            style={{ opacity: fade ? 1 : 0 }}
          >
            {messages[messageIndex]}
          </span>
        </div>

        {/* ── Center: university countdowns ── */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:items-center sm:gap-5">
          {UNIVERSITIES.map((uni, i) => (
            <div key={uni.name} className="flex items-center gap-1.5">
              <span className="text-gray-500 dark:text-white/60 text-xs font-medium">
                {uni.name}
              </span>
              <span className="bg-[#f5694b] text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                {formatCountdown(days[i])}
              </span>
              <span className="text-gray-400 dark:text-white/40 text-xs">
                {formatDate(uni.date)}
                {!uni.confirmed && (
                  <span className="text-gray-300 dark:text-white/30">*</span>
                )}
              </span>
              {i < UNIVERSITIES.length - 1 && (
                <span className="text-gray-200 dark:text-white/20 text-xs ml-1 hidden sm:inline">
                  |
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ── Right: March 2027 pill ── */}
        <div className="flex-shrink-0">
          <span className="flex items-center gap-1.5 border border-[#f5694b]/50 text-[#f5694b] text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] animate-pulse flex-shrink-0" />
            March 2027
          </span>
        </div>

      </div>

      {/* ── Popular listings — own row, so link length never affects the main row ── */}
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-x-3 gap-y-1 flex-wrap mt-2 pt-2 border-t border-orange-100 dark:border-white/10">
        <span className="text-gray-400 dark:text-white/40 text-xs">
          Popular now:
        </span>
        {FEATURED_LISTINGS.map((listing) => (
          <a
            key={listing.name}
            href={listing.href}
            className="text-xs font-semibold text-[#f5694b] hover:underline whitespace-nowrap"
          >
            {listing.name}
          </a>
        ))}
      </div>

      {/* Small disclosure for unconfirmed dates — remove once all four are confirmed */}
      {UNIVERSITIES.some((u) => !u.confirmed) && (
        <div className="max-w-7xl mx-auto mt-1">
          <span className="text-[10px] text-gray-400 dark:text-white/30">
            * estimated date, pending official 2027-1 academic calendar
          </span>
        </div>
      )}
    </div>
  );
};

export default SemesterCountdownBarMarch2027;